import React, { FC } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import Form from '~/components/WizardForm';
import { Select } from '~/components';
import ContentHeader from '~/components/ContentHeader';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import Section from '~/layouts/Section ';
import CustomFields from '~/components/CustomFields';
import buildNamePath from '~/utils/buildNamePath';
import { Stage } from '~/types/Application';
import { useDirection } from '~/hooks/i18nHooks';
import { DirectionProp } from '~/types/Direction';
import { useGetApplication } from '~/controllers/wizard';
import { useHandbook } from '~/controllers/handbook';
import { getTrackingId } from '~/utils/handleGetUtm';
import { useIsTablet } from '~/hooks/useScreenSizes';
import paths from '~/pages/paths';
import { SelectChevronIcon } from '~/img';

const n = buildNamePath('riskQuestionnaire');


const RiskQuestionnaire: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const dir = useDirection();
  const isRTL = dir === 'rtl';
  const location = useLocation();
  const history = useHistory();
  const [form] = Form.useForm();
  const { data }: any = useGetApplication();
  const isMobile = useIsTablet();
  const { data: handbookData } = useHandbook();

  /** Extract select options for a given handbook key. */
  const getHandbookOptions = (key: string) =>
    (handbookData?.handbooks ?? [])
      .find((entry: any) => entry.key === key)
      ?.values?.map((v: any) => ({ value: v.apiValue, label: t(v.localizedValue) })) ?? [];

  const handleParseBeforeSubmit = (values: any) => {
    (window as any).dataLayer?.push({
      event: 'event_formsubmit',
      page_ln: isRTL ? 'ar' : 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      page_section: 'Risk Questionnaire',
      user_id: localStorage.getItem('sob_temp_uid'),
      trackingId: getTrackingId(data),
    });
    return values;
  };

  return (
    <>
      <ContentHeader
        title={t('Risk Questionnaire')}
        subtitle={t('Provide these answers based on your business')}
        stageNumber={3}
      />

      <Container className="RiskQuestionnaire" dir={dir}>
        <Form
          form={form}
          payloadKey="riskQuestionnaire"
          stage={Stage.ksaApplication}
          parseBeforeSubmit={handleParseBeforeSubmit}
        >
          <Section formItemLast>
            <Form.Item
              name={n('monthlyTransactionCount')}
              labelText={t('Expected number of transactions per month')}
              rules={[{ required: true, message: t('This field is required') }]}
            >
              <StyledSelect placeholder={t('Please Select')} options={getHandbookOptions('monthlyTransactionCount')} suffixIcon={<SelectChevronIcon />} />
            </Form.Item>
            <Form.Item
              name={n('monthlySalesAmount')}
              labelText={t('Expected monthly sales amount ({SAR})')}
              rules={[{ required: true, message: t('This field is required') }]}
            >
              <StyledSelect placeholder={t('Please Select')} options={getHandbookOptions('monthlySalesAmount')} suffixIcon={<SelectChevronIcon />} />
            </Form.Item>
            <Form.Item
              name={n('averageTransactionValue')}
              labelText={t('Expected average transaction value ({SAR})')}
              rules={[{ required: true, message: t('This field is required') }]}
            >
              <StyledSelect placeholder={t('Please Select')} options={getHandbookOptions('averageTransactionValue')} suffixIcon={<SelectChevronIcon />} />
            </Form.Item>
            <Form.Item
              name={n('annualRevenue')}
              labelText={t('Expected annual revenue ({SAR})')}
              rules={[{ required: true, message: t('This field is required') }]}
            >
              <StyledSelect placeholder={t('Please Select')} options={getHandbookOptions('annualRevenue')} suffixIcon={<SelectChevronIcon />} />
            </Form.Item>
          </Section>
          <CustomFields />
          <ButtonWrapper>
            <StyledContinueButton id="riskQuestionnaire_continueButton" formName={location.pathname}>
              {t('Continue')}
            </StyledContinueButton>
          </ButtonWrapper>
          {isMobile && (
            <ContactUsButton
              type="text"
              onClick={() => history.push(paths.ksa.contactUs)}
            >
              {t('Contact Us')}
            </ContactUsButton>
          )}
        </Form>
      </Container>
    </>
  );
};

const StyledContinueButton = styled(WizardContinueButton)`
  height: 60px !important;
  min-height: 60px !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  width: 100% !important;
  max-width: 570px !important;
  box-sizing: border-box !important;
  @media (min-width: 769px) {
    height: 52px !important;
    min-height: 52px !important;    
  }
`;

const StyledSelect = styled(Select)`
  width: 100% !important;

  .ant-select-selector {
    display: flex !important;
    padding: 26px 12px !important;
    align-items: center !important;
    gap: 10px !important;
    align-self: stretch !important;
    border-radius: 8px !important;
    border: 1px solid #8F8F8F !important;
    background: #FFF !important;    
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: normal !important;
    display: flex !important;
    align-items: center !important;
  }
`;

const ContactUsButton = styled(Button)`
  width: 100%;
  max-width: 570px;
  margin-top: 20px;
  font-weight: 600;
  color: #0069b1;
`;

const ButtonWrapper = styled.div`
  margin-top: 42px;
  width: 100%;
  max-width: 570px;
  box-sizing: border-box;
`;

const Container = styled.div<DirectionProp>`
  width: 100%;
  max-width: 570px;
  min-width: 0;
  box-sizing: border-box;

  .ant-form-item {
    max-width: 570px;
    width: 100%;
  }

  @media (min-width: 769px) {
    width: 60%;
    max-width: 570px;
  }

  h1 {
    margin-bottom: 16px;
  }
  .RiskQuestionnaire-Title {
    margin-bottom: 56px;
  }
`;

export default RiskQuestionnaire;
